/**
 * Placeholder subscriptions from the June 28 2026 Zuora bulk import.
 *
 * That import wrote one PLACEHOLDER `contact_subscriptions` row per Zuora account
 * (1,459,657 rows on PROD, 401,364 on QA — verified read-only 2026-10-26). Every one
 * has exactly this shape, and no other row does:
 *   - subscription_number = 'SYNTH-<Zuora account number>'
 *   - zuora_subscription_id IS NULL
 *   - product_names empty ('{}')
 *   - subscription_source = 'zuora_bulk_import'
 *   - no subscription_type / payment_frequency / region / policy_id, rate_plan_details '[]'
 * They carry a status (Active / Cancelled) but no product, so rendering one as a card
 * produced a bare "Subscription" title next to the member's real subscription.
 *
 * Display only: these helpers decide what to SHOW. They never change data.
 *
 * Some readers (the MSR member-lookup RPC) do not return `zuora_subscription_id` or
 * `subscription_source`. A field that is ABSENT from the object is treated as unknown
 * and does not block the match; a field that is PRESENT must have the placeholder value.
 * The 'SYNTH-' prefix + no products alone already identify exactly the placeholder set
 * on both environments (0 SYNTH- rows have a product or a Zuora subscription id).
 */

export const PLACEHOLDER_SUBSCRIPTION_PREFIX = "SYNTH-";
export const PLACEHOLDER_SUBSCRIPTION_SOURCE = "zuora_bulk_import";

export interface SubscriptionLike {
  subscription_number?: string | null;
  zuora_subscription_id?: string | null;
  product_names?: string[] | null;
  subscription_source?: string | null;
  status?: string | null;
}

export function isPlaceholderSubscription(sub: SubscriptionLike): boolean {
  if (!sub) {
    return false;
  }
  if (
    typeof sub.subscription_number !== "string" ||
    !sub.subscription_number.startsWith(PLACEHOLDER_SUBSCRIPTION_PREFIX)
  ) {
    return false;
  }
  if (
    sub.zuora_subscription_id !== null &&
    sub.zuora_subscription_id !== undefined &&
    sub.zuora_subscription_id !== ""
  ) {
    return false;
  }
  if ((sub.product_names ?? []).some((p) => (p ?? "").trim() !== "")) {
    return false;
  }
  if (
    "subscription_source" in sub &&
    sub.subscription_source !== PLACEHOLDER_SUBSCRIPTION_SOURCE
  ) {
    return false;
  }
  return true;
}

export interface PartitionedSubscriptions<T extends SubscriptionLike> {
  /** Subscriptions to render as normal cards. */
  visible: T[];
  /** Placeholders hidden because the contact has at least one real subscription. */
  hiddenPlaceholders: T[];
  /**
   * Set only when placeholders are ALL the contact has: render ONE summary card
   * ("Membership — product details not yet loaded from Zuora") instead of one per row.
   */
  placeholderOnly: { status: string | null; count: number } | null;
}

/**
 * Status shown on the single placeholder-only card: Active if any placeholder is
 * active (the member is current), otherwise the first non-empty status.
 */
export function placeholderSummaryStatus(
  subs: SubscriptionLike[],
): string | null {
  const active = subs.find((s) => (s.status ?? "").toLowerCase() === "active");
  if (active) {
    return active.status ?? null;
  }
  return subs.find((s) => s.status)?.status ?? null;
}

export function partitionSubscriptions<T extends SubscriptionLike>(
  subs: T[] | null | undefined,
): PartitionedSubscriptions<T> {
  const list = subs ?? [];
  const real = list.filter((s) => !isPlaceholderSubscription(s));
  const placeholders = list.filter((s) => isPlaceholderSubscription(s));
  if (real.length > 0) {
    return {
      visible: real,
      hiddenPlaceholders: placeholders,
      placeholderOnly: null,
    };
  }
  if (placeholders.length > 0) {
    return {
      visible: [],
      hiddenPlaceholders: [],
      placeholderOnly: {
        status: placeholderSummaryStatus(placeholders),
        count: placeholders.length,
      },
    };
  }
  return { visible: [], hiddenPlaceholders: [], placeholderOnly: null };
}

export const PLACEHOLDER_CARD_TITLE =
  "Membership — product details not yet loaded from Zuora";

export const PLACEHOLDER_CARD_EXPLANATION =
  "This member's account was created by the June 2026 Zuora import, which recorded the membership status but not the product. Product details will appear here once the subscription is synced from Zuora.";

/**
 * Muted line under the real cards. Keeps the hidden rows' status visible (an Active
 * placeholder next to a cancelled real subscription is still information).
 */
export function hiddenPlaceholdersNote(hidden: SubscriptionLike[]): string {
  const counts = new Map<string, number>();
  for (const s of hidden) {
    const k = s.status?.trim() || "no status";
    counts.set(k, (counts.get(k) ?? 0) + 1);
  }
  const statuses =
    hidden.length === 1
      ? [...counts.keys()][0]
      : [...counts.entries()].map(([k, n]) => `${n} ${k}`).join(", ");
  return hidden.length === 1
    ? `1 placeholder record (${statuses}) from the June Zuora import is hidden — it has no product details.`
    : `${hidden.length} placeholder records (${statuses}) from the June Zuora import are hidden — they have no product details.`;
}
